import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetComponent } from 'hooks/useGetComponent';
import {
  componentIds,
  GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS,
} from 'utilities/constants';
import { Fullscript } from '@fullscript/fullscript-js';
import {
  FhirQuestionnaireNames,
  useFullscriptDependentCreateLinkLazyQuery,
  useFullscriptDependentMapMutation,
  useGetFhirProviderByRequestIdQuery,
  useGetFullscriptAllergensByPatientHealthQuestionnaireQuery,
  useGetFullscriptSessionGrantLazyQuery,
  useGetProviderPatientDetailsQuery,
  useOnTreatmentPlanActivatedMutation,
} from 'graphql/generated/remote-schema-hasura';
import { FullscriptAuthLinkState } from 'app/fullscript/fullscript.interface';
import AlertComponent from './alertComponent';
import { interpolateVariables } from '../utilities/functions';
import { useCustomToast } from '../layout/useToast';
import { useGetThirdPartyConfigQuery } from '../graphql/generated/hasura';

type FullScriptEnvironments = 'us' | 'ca' | 'us-snd' | 'ca-snd';

interface SessionGrantData {
  sessionGrant: string;
  fullscriptId: string;
}

export const FullScriptIframe: React.FC<{
  appointmentCodexId?: string;
  patientId: string;
  setError: (error: string) => void;
  patientName: string;
}> = ({ appointmentCodexId, patientId, setError, patientName }) => {
  const [childChecked, setChildChecked] = useState(false);
  const [parentId, setParentId] = useState<string | null>(null);
  const [isChild, setIsChild] = useState(true);

  const { addToast } = useCustomToast();

  const { data: locale, loading } = useGetComponent({
    locale: 'en',
    componentId: componentIds.VIRTUAL_PHARMACY_MODAL,
  });

  const { loading: loadingPatientData } = useGetProviderPatientDetailsQuery({
    variables: {
      patientCodexId: patientId as string,
    },
    onCompleted: (data) => {
      setIsChild(
        !!data.getFHIRPatientbyCodexIDParameter.users[0].parentCodexId,
      );
      setParentId(
        data.getFHIRPatientbyCodexIDParameter.users[0].parentCodexId || null,
      );
    },
  });

  const readyUserForFullScript = childChecked || !loadingPatientData;
  const {
    data,
    loading: loadConfig,
    refetch,
  } = useGetThirdPartyConfigQuery({
    context: {
      ignoreGlobalErrors: true,
    },
  });

  const fullscriptClient = useCallback(async () => {
    const config = await refetch();
    return Fullscript({
      publicKey: config?.data?.frontEndAuthConfigs?.fullscript?.clientId,
      env: config?.data?.frontEndAuthConfigs?.fullscript
        ?.environment as FullScriptEnvironments,
    });
  }, [refetch]);

  const authorizationUrl = useMemo(
    () => data?.frontEndAuthConfigs?.fullscript?.authorizationUrl,
    [data],
  );

  const [sessionGrantData, setSessionData] = useState<SessionGrantData | null>(
    null,
  );

  const [getSessionGrant] = useGetFullscriptSessionGrantLazyQuery({
    fetchPolicy: 'network-only',
    onError: () =>
      addToast({
        type: 'warning',
        message:
          'Error with fullscript module please reload the frame. Please try again later',
      }),
  });

  const [onTreatmentPlanActivated] = useOnTreatmentPlanActivatedMutation({});
  const [fullscriptDependentMap] = useFullscriptDependentMapMutation({});
  const [fullscriptDependentCreateLink] =
    useFullscriptDependentCreateLinkLazyQuery({
      onCompleted: async ({ fullscriptDependentCreateLink: payload }) => {
        const { childFullscriptId } = payload;

        if (childFullscriptId) {
          setChildChecked(true);
          return;
        }

        const result = await getSessionGrant({
          variables: {
            patientCodexId: parentId as string,
          },
        });

        if (!result.data?.getSessionGrant.sessionGrant) return;

        const Client = await fullscriptClient();
        const platform = Client.create('platform', {
          secretToken: result.data?.getSessionGrant.sessionGrant,
        });

        platform.on('patient.selected', async (evt) => {
          const fullscriptPatientId = evt?.data?.patient?.id;

          if (!fullscriptPatientId) return;

          try {
            const result = await fullscriptDependentMap({
              variables: {
                fullscriptPatientId,
                childCodexId: patientId,
              },
            });

            if (result.errors?.length) {
              setError(result.errors?.[0]?.message);
              platform.unmount?.();
              throw new Error(result.errors?.[0]?.message);
            }

            setChildChecked(true);

            platform.unmount?.();
          } catch (error) {
            console.error(error);
          } finally {
            platform.unmount?.();
          }
        });

        platform.mount('fs-platform');
      },
    });

  const { data: providerData } = useGetFhirProviderByRequestIdQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!providerData || !patientId || !readyUserForFullScript) return;
    const fullscriptProviderId =
      providerData?.getFHIRProviderByRequestId.providers[0]?.fullscriptId;
    if (!fullscriptProviderId) {
      const state: FullscriptAuthLinkState = {
        patientId: patientId,
      };
      window.location.href = `${authorizationUrl}&state=${encodeURI(
        JSON.stringify(state),
      )}`;
      return;
    }
    (async () => {
      if (isChild) {
        await fullscriptDependentCreateLink({
          variables: {
            childCodexId: patientId,
          },
          fetchPolicy: 'network-only',
        });
      }
      const result = await getSessionGrant({
        variables: {
          patientCodexId: patientId as string,
        },
      });

      if (result?.data?.getSessionGrant) {
        setSessionData({
          sessionGrant: result?.data?.getSessionGrant.sessionGrant,
          fullscriptId: result?.data?.getSessionGrant.patientFullscriptId,
        });
      }
    })();
  }, [
    getSessionGrant,
    patientId,
    providerData,
    authorizationUrl,
    readyUserForFullScript,
    isChild,
    fullscriptDependentCreateLink,
  ]);

  const handleTreatmentPlanActivated = useCallback(
    (treatmentPlanId: string) => {
      onTreatmentPlanActivated({
        variables: {
          patientCodexId: patientId as string,
          treatmentPlanId,
          appointmentCodexId,
        },
      });
    },
    [appointmentCodexId, onTreatmentPlanActivated, patientId],
  );

  useEffect(() => {
    const getPlan = async () => {
      if (sessionGrantData) {
        const Client = await fullscriptClient();
        const treatmentPlanFeature = Client.create('treatmentPlan', {
          secretToken: sessionGrantData.sessionGrant,
          patient: {
            id: sessionGrantData.fullscriptId,
          },
        });
        treatmentPlanFeature.on('treatmentPlan.activated', (event) => {
          handleTreatmentPlanActivated(event.data.treatmentPlan.id);
        });
        treatmentPlanFeature.mount('treatment-plan-container');
      }
    };

    getPlan();
  }, [handleTreatmentPlanActivated, sessionGrantData, fullscriptClient]);

  if (loading || !locale || loadConfig) return null;

  if (!data?.frontEndAuthConfigs?.fullscript) return null;

  return (
    <div className="h-full w-full flex flex-col items-center">
      <div className="w-full">
        <FullScriptAllergensBanner
          patientId={patientId}
          locale={locale}
          patientName={patientName}
        />
      </div>
      <div className="h-full">
        <div
          className={`desktop:w-[1008px] h-full min-h-[640px] ${
            sessionGrantData ? 'hidden' : 'flex'
          }`}
        >
          <div style={{ width: '100%', height: '100%' }} id="fs-platform" />
        </div>

        <div
          className={`desktop:w-[1052px] h-full min-h-[640px] ${
            sessionGrantData ? 'flex' : 'hidden'
          }`}
        >
          <div
            style={{ width: '100%', height: '100%' }}
            id="treatment-plan-container"
          />
        </div>
      </div>
    </div>
  );
};

function FullScriptAllergensBanner({
  locale,
  patientId,
  patientName,
}: {
  locale: Record<string, string>;
  patientId: string;
  patientName: string;
}) {
  const [allergens, setAllergens] = useState<string[]>([]);

  useGetFullscriptAllergensByPatientHealthQuestionnaireQuery({
    variables: {
      codexPatientId: patientId,
      identifier: FhirQuestionnaireNames.HealthQuestionnaire,
      system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
    },
    onCompleted: (data) => {
      const allergenNames =
        data.getFullscriptAllergensFromQuestionnaireResponse.fullscript_allergens.map(
          (allergen) => {
            return allergen.fullscript_name;
          },
        );

      setAllergens(allergenNames);
    },
  });

  if (!allergens.length) return null;

  return (
    <div className="mb-30 px-5 desktop:px-11 relative -top-2.5">
      <AlertComponent
        desktopItemsAlign={'desktop:items-start'}
        type="info"
        customContent={
          <div className="text-amber-border">
            <div className="text-amber-border font-semibold">
              <p>
                {interpolateVariables(locale?.patientAlergensTitle, {
                  patientName: patientName,
                })}
              </p>
              <p>{locale?.patientAlergensTitle2}</p>
            </div>

            <div className="text-amber-border">{allergens?.join(', ')}</div>
          </div>
        }
      />
    </div>
  );
}
